import * as React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { CircleDashedIcon } from "@phosphor-icons/react/dist/ssr/CircleDashed";
import { CircleNotchIcon } from "@phosphor-icons/react/dist/ssr/CircleNotch";

import type { CampaignRequestDetail, CampaignRequestEvent, RequestState } from "@/hooks/api/use-campaign-requests";

import { compareEventOrder, formatDate, formatDateTime } from "../constants";
import { labelOf } from "../humanise";

/**
 * The progress timeline of UX §12, built from `GET /campaign-requests/:request/events`.
 *
 * Note 1 on that section is the whole design: "The timeline is written in their language, not
 * ours. 'Building your agents — 2 of 3 done', not `in_preparation`. The state machine is behind
 * it; the copy isn't the state name." So every row here is copy, keyed by the state the event
 * moved to, and no raw state name reaches the screen — `StateChip` keeps the machine's own name
 * in a tooltip for the one place support needs to join the two.
 *
 * What has not happened yet is drawn as well as what has. A client reading this wants to know
 * what is left, and a list that stops at "now" answers half the question.
 */

export interface RequestTimelineProps {
	request: CampaignRequestDetail;
	events: CampaignRequestEvent[];
	isLoading?: boolean;
}

/** What each arrival means to the client, rather than what it is called in the database. */
const ARRIVED: Record<RequestState, string> = {
	draft: "Request started",
	submitted: "Request submitted",
	quoted: "Quote issued",
	awaiting_confirmation: "Quote ready for your confirmation",
	confirmed: "Quote confirmed",
	in_preparation: "Building your agents",
	ready: "Final checks passed",
	running: "Calling started",
	completed: "Campaign finished",
	changes_requested: "Changes requested",
	cancelled: "Request cancelled",
};

/** The same steps phrased as things still to come. */
const AHEAD: Partial<Record<RequestState, string>> = {
	submitted: "Send the request to AdaptiveX",
	quoted: "We price it",
	awaiting_confirmation: "You confirm the quote",
	confirmed: "Quote confirmed",
	in_preparation: "We build your agents",
	ready: "Final checks",
	running: "First calls",
	completed: "Campaign finishes",
};

/** The forward path. Cancellation and change requests are moves off it, never steps along it. */
const HAPPY_PATH: RequestState[] = [
	"draft",
	"submitted",
	"quoted",
	"awaiting_confirmation",
	"confirmed",
	"in_preparation",
	"ready",
	"running",
	"completed",
];

const TERMINAL = new Set<RequestState>(["completed", "cancelled"]);

export interface TimelineItem {
	key: string;
	label: string;
	detail?: string | null;
	time: string;
	status: "done" | "now" | "ahead";
}

/** The earliest date any market starts dialing — what "First calls" is scheduled for. */
function firstDialDate(request: CampaignRequestDetail): string | null {
	const starts = (request.marketSchedules.map((market) => market.dialStartDate).filter(Boolean) as string[]).toSorted();

	return starts[0] ?? null;
}

/**
 * The whole progress list, exported because the summary tab reads the same two rows off it.
 *
 * The five-second answer a client wants — where is it now, what happens next — is the "now" item
 * and the first "ahead" item of this list. Deriving it here rather than writing a second, shorter
 * story of the same request is what stops the two disagreeing.
 */
export function buildTimeline(request: CampaignRequestDetail, events: CampaignRequestEvent[]): TimelineItem[] {
	// By `(at, seq)`, never by `at` alone — see `compareEventOrder`. Re-established here rather
	// than trusted off the API because this component is handed an array and cannot know who
	// assembled it, and the cost of being wrong is a confirmation drawn above the submission that
	// caused it.
	const ordered = [...events].toSorted(compareEventOrder);
	const items: TimelineItem[] = ordered.map((event, index) => ({
		key: event.id,
		// The one place a state the API grew and this build has not learned could reach a client
		// verbatim. `in_preparation` is exactly what §12 note 1 says must never appear here, so the
		// fallback humanises rather than printing the machine's own word.
		label: labelOf(ARRIVED, event.toState),
		// The name is the client's own record of who authorised what, so it is never dropped.
		detail: event.actorName ? `${event.actorName}${event.reason ? ` — ${event.reason}` : ""}` : event.reason,
		time: formatDateTime(event.at),
		status: index === ordered.length - 1 && !TERMINAL.has(event.toState) ? "now" : "done",
	}));

	// A request created and never moved has no events at all, and still has to read as something.
	if (items.length === 0) {
		items.push({
			key: "created",
			label: ARRIVED.draft,
			time: formatDateTime(request.createdAt),
			status: request.state === "draft" ? "now" : "done",
		});
	}

	if (request.state === "in_preparation") {
		const last = items.at(-1);
		if (last && last.status === "now") {
			last.detail =
				request.agents.length > 0
					? `${request.agents.length} agent${request.agents.length === 1 ? "" : "s"} for ${request.marketSchedules.length} market${request.marketSchedules.length === 1 ? "" : "s"}`
					: last.detail;
		}
	}

	if (TERMINAL.has(request.state) || request.state === "changes_requested") return items;

	const position = HAPPY_PATH.indexOf(request.state);
	if (position === -1) return items;

	const dialDate = firstDialDate(request);

	for (const state of HAPPY_PATH.slice(position + 1)) {
		items.push({
			key: `ahead-${state}`,
			label:
				state === "running" && dialDate ? `First calls — ${formatDate(dialDate)}` : (AHEAD[state] ?? ARRIVED[state]),
			time: state === "running" && dialDate ? "scheduled" : "—",
			status: "ahead",
		});
	}

	return items;
}

function StatusMark({ status }: { status: TimelineItem["status"] }): React.JSX.Element {
	if (status === "done") return <CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" size={18} />;
	if (status === "now") return <CircleNotchIcon color="var(--mui-palette-primary-main)" weight="bold" size={18} />;
	return <CircleDashedIcon color="var(--mui-palette-text-disabled)" size={18} />;
}

export function RequestTimeline({ request, events, isLoading = false }: RequestTimelineProps): React.JSX.Element {
	const items = React.useMemo(() => buildTimeline(request, events), [request, events]);

	if (isLoading) {
		return (
			<Stack spacing={1.5}>
				{Array.from({ length: 5 }).map((_, index) => (
					<Skeleton key={index} height={24} variant="rounded" />
				))}
			</Stack>
		);
	}

	return (
		<Stack spacing={0}>
			{items.map((item, index) => (
				<Box
					key={item.key}
					sx={{
						display: "flex",
						gap: 1.5,
						opacity: item.status === "ahead" ? 0.6 : 1,
						pb: index === items.length - 1 ? 0 : 2,
						position: "relative",
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
						<StatusMark status={item.status} />
						{index === items.length - 1 ? null : (
							<Box sx={{ bgcolor: "divider", flex: "1 1 auto", mt: 0.5, width: "1px" }} />
						)}
					</Box>

					<Box sx={{ display: "flex", flex: "1 1 auto", gap: 2, justifyContent: "space-between", pb: 0.5 }}>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: item.status === "now" ? 700 : 500 }}>
								{item.label}
							</Typography>
							{item.detail ? (
								<Typography variant="caption" color="text.secondary">
									{item.detail}
								</Typography>
							) : null}
						</Box>

						<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
							{item.time}
						</Typography>
					</Box>
				</Box>
			))}
		</Stack>
	);
}
